import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useI18n } from '../../../i18n';

const KNOWN_ERRORS = new Set(['noTables', 'noCharts']);

/**
 * Toolbar "Export" button. `formats` are `{ id, onExport }`; `onExport` may
 * return a promise, during which the button shows progress.
 */
const ExportMenu = ({ formats, disabled }) => {
  const { t } = useI18n();
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = async (format) => {
    setAnchor(null);
    setBusy(true);
    setError(null);
    try {
      await format.onExport();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[export] ${format.id} failed`, err);
      setError(KNOWN_ERRORS.has(err?.code) ? t(`export.${err.code}`) : t('export.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        data-export-ignore="true"
        startIcon={busy ? <CircularProgress size={16} /> : <FileDownloadIcon />}
        disabled={disabled || busy}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
      >
        {busy ? t('export.inProgress') : t('export.button')}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {formats.map((format) => (
          <MenuItem key={format.id} onClick={() => run(format)}>
            <ListItemText
              primary={t(`export.format.${format.id}`)}
              secondary={t(`export.formatHint.${format.id}`)}
            />
          </MenuItem>
        ))}
      </Menu>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportMenu;
